import React, { useEffect, useRef, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  TableSortLabel,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Stack,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { format } from "date-fns";
import {
  fetchAllOrders,
  fetchAllOrganizations,
  crmSliceSelector,
} from "../../../redux/features/crmSlice";
import { getPaymentType } from "../../../utils";

export default function CRMOrders() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const crmState = useSelector((state) => crmSliceSelector(state));
  const {
    allOrders,
    allOrdersLoading,
    allOrdersLastVisible,
    allOrdersAllLoaded,
    organizationsMap,
  } = crmState;

  const observerTarget = useRef(null);
  const fetchAttemptedRef = useRef(false);

  // Parse sort params from URL query string
  const sortParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      sortBy: searchParams.get("sortBy") || "createdAt",
      sortOrder: searchParams.get("sortOrder") || "desc",
    };
  }, [location.search]);

  // Parse filter params from URL query string
  // Format: filter_include_field=value1,value2&filter_exclude_field=value1
  const filterParams = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const filters = { include: {}, exclude: {} };

    searchParams.forEach((value, key) => {
      if (key.startsWith("filter_include_")) {
        const field = key.replace("filter_include_", "");
        filters.include[field] = value.split(",").filter(Boolean);
      } else if (key.startsWith("filter_exclude_")) {
        const field = key.replace("filter_exclude_", "");
        filters.exclude[field] = value.split(",").filter(Boolean);
      }
    });

    return filters;
  }, [location.search]);

  const [contextMenu, setContextMenu] = useState(null);
  const [contextMenuField, setContextMenuField] = useState(null);
  const [contextMenuValue, setContextMenuValue] = useState(null);
  const [chipContextMenu, setChipContextMenu] = useState(null);
  const [chipContextMenuData, setChipContextMenuData] = useState(null);

  useEffect(() => {
    // Fetch organizations if not already fetched (needed for organization names)
    if (!crmState.organizationsFetched && !crmState.organizationsLoading) {
      dispatch(fetchAllOrganizations());
    }
  }, [dispatch, crmState.organizationsFetched, crmState.organizationsLoading]);

  // Track previous sort params, filter params, and pathname to detect changes
  const prevSortParamsRef = useRef(sortParams);
  const prevFilterParamsRef = useRef(filterParams);
  const prevPathnameRef = useRef(location.pathname);
  const prevSearchRef = useRef(location.search);

  useEffect(() => {
    // Check if pathname changed (user navigated away and back)
    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    // Check if search params changed (filters or sort in URL)
    const searchChanged = prevSearchRef.current !== location.search;

    if (pathnameChanged) {
      prevPathnameRef.current = location.pathname;
      fetchAttemptedRef.current = false;
    }

    if (searchChanged) {
      prevSearchRef.current = location.search;
    }

    // Check if sort params changed
    const sortChanged =
      prevSortParamsRef.current.sortBy !== sortParams.sortBy ||
      prevSortParamsRef.current.sortOrder !== sortParams.sortOrder;

    // Check if filter params changed
    const filterChanged =
      JSON.stringify(prevFilterParamsRef.current) !==
      JSON.stringify(filterParams);

    // Initial fetch when component mounts or pathname changes
    const hasNoOrders = (allOrders || []).length === 0;

    if ((sortChanged || filterChanged || searchChanged) && !allOrdersLoading) {
      // Sort, filter, or search params changed - always reset and refetch
      prevSortParamsRef.current = sortParams;
      prevFilterParamsRef.current = filterParams;
      fetchAttemptedRef.current = true;
      dispatch(
        fetchAllOrders({
          reset: true,
          pageSize: 50,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
          filters: filterParams,
        }),
      );
    } else if (
      (hasNoOrders || pathnameChanged) &&
      !allOrdersLoading &&
      !fetchAttemptedRef.current
    ) {
      // Initial fetch when component mounts or pathname changes
      fetchAttemptedRef.current = true;
      prevSortParamsRef.current = sortParams;
      prevFilterParamsRef.current = filterParams;
      dispatch(
        fetchAllOrders({
          reset: true,
          pageSize: 50,
          sortBy: sortParams.sortBy,
          sortOrder: sortParams.sortOrder,
          filters: filterParams,
        }),
      );
    }
  }, [
    location.pathname,
    location.search,
    dispatch,
    allOrders,
    allOrdersLoading,
    sortParams,
    filterParams,
  ]);

  // Continuous loading with Intersection Observer
  useEffect(() => {
    const loadMore = () => {
      if (
        !allOrdersLoading &&
        !allOrdersAllLoaded &&
        allOrdersLastVisible &&
        fetchAttemptedRef.current
      ) {
        dispatch(
          fetchAllOrders({
            lastVisible: allOrdersLastVisible,
            pageSize: 50,
            sortBy: sortParams.sortBy,
            sortOrder: sortParams.sortOrder,
            filters: filterParams,
          }),
        );
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [
    allOrdersLoading,
    allOrdersAllLoaded,
    allOrdersLastVisible,
    sortParams,
    filterParams,
    dispatch,
  ]);

  const getOrganizationName = (organizationId) => {
    if (!organizationId) return "N/A";
    const org = organizationsMap?.[organizationId];
    if (org && typeof org === "object" && typeof org.name === "string") {
      return org.name;
    }
    return organizationId || "N/A";
  };

  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    let date = timestamp;
    if (date?.toDate) {
      return format(date.toDate(), "MMM d, yyyy h:mm a");
    }
    return "N/A";
  };

  const getPurchaserDisplay = (order) => {
    // Root orders collection (CRM) syncs purchaserName/purchaserEmail from orderFormFields
    if (order?.purchaserName != null || order?.purchaserEmail != null) {
      return {
        purchaserName: order.purchaserName || "N/A",
        purchaserEmail: order.purchaserEmail || "N/A",
      };
    }
    // Fallback: derive from orderFormFields (e.g. org-level order list)
    const fields = order?.orderFormFields ?? [];
    const byKey = Object.fromEntries(
      fields.map((f) => [f.key || f.name, f]).filter(([, f]) => f != null),
    );
    const firstLast = [byKey.firstName?.value, byKey.lastName?.value]
      .filter(Boolean)
      .join(" ")
      .trim();
    const name = byKey.name?.value ?? (firstLast || null);
    const email = byKey.email?.value ?? null;
    return {
      purchaserName: name || "N/A",
      purchaserEmail: email || "N/A",
    };
  };

  // Map display column names to Firestore field names
  const getFirestoreFieldName = (columnName) => {
    const fieldMap = {
      eventName: "eventName",
      organization: "organizationId", // Sort by organizationId, not display name
      amount: "totalUsdCents",
      paymentType: "paymentMethod",
      source: "sourceType",
      affiliate: "affiliateName",
      createdAt: "createdAt",
      purchaserEmail: "purchaserEmail",
    };
    return fieldMap[columnName];
  };

  const handleRequestSort = (columnName) => {
    const firestoreField = getFirestoreFieldName(columnName);
    if (!firestoreField) {
      // Column is not sortable in Firestore
      return;
    }

    const isAsc =
      sortParams.sortBy === firestoreField && sortParams.sortOrder === "asc";
    const newSortOrder = isAsc ? "desc" : "asc";

    // Update URL query parameters - the effect will handle refetching
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("sortBy", firestoreField);
    searchParams.set("sortOrder", newSortOrder);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const handleCellClick = (event, field, rawValue, displayValue) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setContextMenuField(field);
    setContextMenuValue({ rawValue, displayValue });
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setContextMenuField(null);
    setContextMenuValue(null);
  };

  const handleChipClick = (event, field, value, currentType) => {
    event.stopPropagation();
    setChipContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
    });
    setChipContextMenuData({ field, value, currentType });
  };

  const handleChipContextMenuClose = () => {
    setChipContextMenu(null);
    setChipContextMenuData(null);
  };

  const handleFlipFilter = () => {
    if (chipContextMenuData) {
      const { field, value, currentType } = chipContextMenuData;
      // Flip between include and exclude
      const newType = currentType === "include" ? "exclude" : "include";
      toggleFilter(field, value, newType, true);
    }
    handleChipContextMenuClose();
  };

  const handleFilterBy = () => {
    if (contextMenuField && contextMenuValue) {
      const firestoreField = getFirestoreFieldName(contextMenuField);
      if (firestoreField) {
        // Use rawValue if available (the actual Firestore field value), otherwise use displayValue
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(firestoreField, filterValue, "include", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  const handleExclude = () => {
    if (contextMenuField && contextMenuValue) {
      const firestoreField = getFirestoreFieldName(contextMenuField);
      if (firestoreField) {
        // Use rawValue if available (the actual Firestore field value), otherwise use displayValue
        const filterValue =
          contextMenuValue.rawValue || contextMenuValue.displayValue;
        const isRawValue = !!contextMenuValue.rawValue;
        toggleFilter(firestoreField, filterValue, "exclude", isRawValue);
      }
    }
    handleContextMenuClose();
  };

  // Convert filter value back to Firestore field value (for organization, convert name back to ID)
  const getFilterValue = (field, displayValue) => {
    if (field === "organizationId") {
      // Find the organizationId that matches this display name
      const order = allOrders.find(
        (o) => getOrganizationName(o.organizationId) === displayValue,
      );
      return order?.organizationId || displayValue;
    }
    return displayValue;
  };

  // Convert Firestore field value to display value (for payment method, convert "GOOGLE" to "Google Pay")
  const getDisplayValue = (field, rawValue) => {
    if (!rawValue) return rawValue;

    if (field === "paymentMethod") {
      const order = { paymentMethod: rawValue };
      return getPaymentType(order);
    }
    if (field === "organizationId") {
      return getOrganizationName(rawValue);
    }
    if (field === "totalUsdCents") {
      // Convert cents to formatted currency
      const cents = parseInt(rawValue, 10);
      if (isNaN(cents)) return rawValue;
      return formatCurrency(cents);
    }
    if (field === "sourceType") {
      // Format source type for display
      if (rawValue === "retail_web") {
        return "Web Order";
      }
      if (rawValue === "manual_user") {
        return "Manual Order";
      }
      // Convert snake_case to Title Case
      return rawValue
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    }
    // For other fields (eventName, affiliateName, createdAt), return as-is
    // createdAt might be a timestamp, but we'll handle that if needed
    return rawValue;
  };

  const toggleFilter = (field, value, type, isRawValue = false) => {
    // If value is already a raw Firestore value, use it directly
    // Otherwise, convert display value to Firestore field value (e.g., org name -> org ID)
    const filterValue = isRawValue ? value : getFilterValue(field, value);

    const searchParams = new URLSearchParams(location.search);
    const includeKey = `filter_include_${field}`;
    const excludeKey = `filter_exclude_${field}`;

    if (type === "include") {
      const currentInclude = filterParams.include[field] || [];
      const newInclude = currentInclude.includes(filterValue)
        ? currentInclude.filter((v) => v !== filterValue)
        : [...currentInclude, filterValue];

      // Remove from exclude if it's there
      const newExclude = (filterParams.exclude[field] || []).filter(
        (v) => v !== filterValue,
      );

      if (newInclude.length > 0) {
        searchParams.set(includeKey, newInclude.join(","));
      } else {
        searchParams.delete(includeKey);
      }

      if (newExclude.length > 0) {
        searchParams.set(excludeKey, newExclude.join(","));
      } else {
        searchParams.delete(excludeKey);
      }
    } else {
      // Allow multiple exclude filters (client-side filtering handles this)
      const currentExclude = filterParams.exclude[field] || [];
      const newExclude = currentExclude.includes(filterValue)
        ? currentExclude.filter((v) => v !== filterValue)
        : [...currentExclude, filterValue];

      // Remove from include if it's there
      const newInclude = (filterParams.include[field] || []).filter(
        (v) => v !== filterValue,
      );

      if (newExclude.length > 0) {
        searchParams.set(excludeKey, newExclude.join(","));
      } else {
        searchParams.delete(excludeKey);
      }

      if (newInclude.length > 0) {
        searchParams.set(includeKey, newInclude.join(","));
      } else {
        searchParams.delete(includeKey);
      }
    }

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  const clearAllFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    // Collect all filter keys first, then delete them
    // (can't delete while iterating)
    const keysToDelete = [];
    searchParams.forEach((value, key) => {
      if (
        key.startsWith("filter_include_") ||
        key.startsWith("filter_exclude_")
      ) {
        keysToDelete.push(key);
      }
    });
    // Delete all filter keys
    keysToDelete.forEach((key) => {
      searchParams.delete(key);
    });
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    Object.values(filterParams.include || {}).forEach((values) => {
      count += values.length;
    });
    Object.values(filterParams.exclude || {}).forEach((values) => {
      count += values.length;
    });
    return count;
  }, [filterParams]);

  // Helper component for filterable table cell
  const FilterableCell = ({ field, value, rawValue, children, ...props }) => {
    const firestoreField = getFirestoreFieldName(field);

    if (!firestoreField) {
      return <TableCell {...props}>{children}</TableCell>;
    }

    // Disable filtering for "N/A" values or null/undefined raw values
    const isFilterable =
      value !== "N/A" &&
      rawValue !== null &&
      rawValue !== undefined &&
      rawValue !== "N/A";

    const hasFilter =
      filterParams.include[firestoreField]?.length > 0 ||
      filterParams.exclude[firestoreField]?.length > 0;

    return (
      <TableCell
        {...props}
        onClick={
          isFilterable
            ? (e) => handleCellClick(e, field, rawValue, value)
            : undefined
        }
        sx={{
          cursor: isFilterable ? "pointer" : "default",
          position: "relative",
          ...(isFilterable && {
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }),
          ...(hasFilter && {
            backgroundColor: "action.selected",
          }),
          ...props.sx,
        }}
      >
        {children}
      </TableCell>
    );
  };

  return (
    <Container>
      <Typography variant="h2" paddingTop={"20px"}>
        Orders
      </Typography>

      {/* Active Filters */}
      {activeFilterCount > 0 && (
        <Box marginTop={2} marginBottom={2}>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            alignItems="center"
          >
            <Chip
              label="Clear all"
              onClick={clearAllFilters}
              size="small"
              variant="outlined"
            />
            {Object.entries(filterParams.include || {}).map(([field, values]) =>
              values.map((value) => {
                // Convert raw value to display value (e.g., "GOOGLE" -> "Google Pay", org ID -> org name)
                const displayValue = getDisplayValue(field, value);
                const fieldLabel =
                  field === "organizationId"
                    ? "organization"
                    : field === "paymentMethod"
                    ? "payment type"
                    : field === "totalUsdCents"
                    ? "amount"
                    : field === "sourceType"
                    ? "source"
                    : field === "affiliateName"
                    ? "affiliate"
                    : field === "eventName"
                    ? "event"
                    : field === "createdAt"
                    ? "created"
                    : field === "purchaserEmail"
                    ? "email"
                    : field;
                return (
                  <Chip
                    key={`include-${field}-${value}`}
                    label={`${fieldLabel}: ${displayValue}`}
                    onClick={(e) => handleChipClick(e, field, value, "include")}
                    onDelete={(e) => {
                      e.stopPropagation();
                      // When deleting, we need to use the raw value, not the display value
                      toggleFilter(field, value, "include", true);
                    }}
                    color="success"
                    size="small"
                    icon={<FilterListIcon />}
                    sx={{ cursor: "pointer" }}
                  />
                );
              }),
            )}
            {Object.entries(filterParams.exclude || {}).map(([field, values]) =>
              values.map((value) => {
                // Convert raw value to display value (e.g., "GOOGLE" -> "Google Pay", org ID -> org name)
                const displayValue = getDisplayValue(field, value);
                const fieldLabel =
                  field === "organizationId"
                    ? "organization"
                    : field === "paymentMethod"
                    ? "payment type"
                    : field === "totalUsdCents"
                    ? "amount"
                    : field === "sourceType"
                    ? "source"
                    : field === "affiliateName"
                    ? "affiliate"
                    : field === "eventName"
                    ? "event"
                    : field === "createdAt"
                    ? "created"
                    : field === "purchaserEmail"
                    ? "email"
                    : field;
                return (
                  <Chip
                    key={`exclude-${field}-${value}`}
                    label={`${fieldLabel}: ${displayValue} (excluded)`}
                    onClick={(e) => handleChipClick(e, field, value, "exclude")}
                    onDelete={(e) => {
                      e.stopPropagation();
                      // When deleting, we need to use the raw value, not the display value
                      toggleFilter(field, value, "exclude", true);
                    }}
                    color="error"
                    size="small"
                    icon={<RemoveCircleIcon />}
                    sx={{ cursor: "pointer" }}
                  />
                );
              }),
            )}
          </Stack>
        </Box>
      )}

      {allOrdersLoading && allOrders.length === 0 && (
        <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
      )}

      {!allOrdersLoading &&
        allOrders.length === 0 &&
        fetchAttemptedRef.current && (
          <Box marginTop={4}>
            <Typography variant="body1" color="text.secondary">
              No orders found
            </Typography>
          </Box>
        )}

      {allOrders && allOrders.length > 0 && (
        <Box marginTop={4}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Order ID</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "eventName"}
                      direction={
                        sortParams.sortBy === "eventName"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleRequestSort("eventName")}
                    >
                      Event
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "organizationId"}
                      direction={
                        sortParams.sortBy === "organizationId"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleRequestSort("organization")}
                    >
                      Organization
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "totalUsdCents"}
                      direction={
                        sortParams.sortBy === "totalUsdCents"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleRequestSort("amount")}
                    >
                      Amount
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Purchaser Name</TableCell>
                  <TableCell>Purchaser Email</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortParams.sortBy === "createdAt"}
                      direction={
                        sortParams.sortBy === "createdAt"
                          ? sortParams.sortOrder
                          : "asc"
                      }
                      onClick={() => handleRequestSort("createdAt")}
                    >
                      Starts At
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allOrders.map((order) => {
                  const {
                    id,
                    eventName,
                    organizationId,
                    totalUsdCents,
                    createdAt,
                  } = order;

                  const eventNameDisplay =
                    typeof eventName === "string"
                      ? eventName
                      : eventName
                      ? String(eventName)
                      : "N/A";
                  const organizationName = getOrganizationName(organizationId);
                  const createdAtDisplay = formatDate(createdAt);
                  const { purchaserName, purchaserEmail } =
                    getPurchaserDisplay(order);

                  // For amount, we need to use the numeric value for filtering, not the formatted currency
                  const amountRawValue = totalUsdCents?.toString() || null;

                  return (
                    <TableRow key={id}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component={Link}
                          to={`/crm/orders/${organizationId}/${id}`}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {id}
                        </Typography>
                      </TableCell>
                      <FilterableCell
                        field="eventName"
                        value={eventNameDisplay}
                        rawValue={eventName || null}
                      >
                        <Typography variant="body2">
                          {eventNameDisplay}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="organization"
                        value={organizationName}
                        rawValue={organizationId}
                      >
                        <Typography
                          variant="body2"
                          component={Link}
                          to={`/crm/organizations/${organizationId}`}
                          sx={{
                            textDecoration: "none",
                            color: "primary.main",
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          {organizationName}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="amount"
                        value={formatCurrency(totalUsdCents)}
                        rawValue={amountRawValue}
                      >
                        <Typography variant="body2">
                          {formatCurrency(totalUsdCents)}
                        </Typography>
                      </FilterableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {purchaserName}
                        </Typography>
                      </TableCell>
                      <FilterableCell
                        field="purchaserEmail"
                        value={purchaserEmail}
                        rawValue={order.purchaserEmail || null}
                      >
                        <Typography variant="body2">
                          {purchaserEmail}
                        </Typography>
                      </FilterableCell>
                      <FilterableCell
                        field="createdAt"
                        value={createdAtDisplay}
                      >
                        <Typography variant="body2">
                          {createdAtDisplay}
                        </Typography>
                      </FilterableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {allOrdersLoading && (
            <LinearProgress sx={{ marginTop: 2, marginBottom: 2 }} />
          )}

          {allOrdersAllLoaded && (
            <Box marginTop={2} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                All orders loaded
              </Typography>
            </Box>
          )}

          {!allOrdersAllLoaded && (
            <div ref={observerTarget} style={{ height: "20px" }} />
          )}
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFilterBy}>
          <ListItemIcon>
            <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
          </ListItemIcon>
          <ListItemText
            primary="Filter by..."
            secondary={
              contextMenuValue
                ? `Show only "${contextMenuValue.displayValue}"`
                : ""
            }
          />
        </MenuItem>
        <MenuItem onClick={handleExclude}>
          <ListItemIcon>
            <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText
            primary="Exclude..."
            secondary={
              contextMenuValue ? `Hide "${contextMenuValue.displayValue}"` : ""
            }
          />
        </MenuItem>
      </Menu>

      {/* Chip Context Menu */}
      <Menu
        open={chipContextMenu !== null}
        onClose={handleChipContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          chipContextMenu !== null
            ? { top: chipContextMenu.mouseY, left: chipContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleFlipFilter}>
          <ListItemIcon>
            {chipContextMenuData?.currentType === "include" ? (
              <RemoveCircleIcon fontSize="small" sx={{ color: "error.main" }} />
            ) : (
              <FilterListIcon fontSize="small" sx={{ color: "success.main" }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              chipContextMenuData?.currentType === "include"
                ? "Switch to Exclude"
                : "Switch to Include"
            }
            secondary={
              chipContextMenuData?.currentType === "include"
                ? "Hide this value instead"
                : "Show only this value"
            }
          />
        </MenuItem>
      </Menu>
    </Container>
  );
}
